import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useTenant } from '@/contexts/TenantProvider'
import type { Json } from '@/types/database.types'

export interface ClientDNARecord {
  id: string
  tenant_id: string
  client_id: string
  booking_id: string | null
  before_image_url: string | null
  after_image_url: string | null
  chemistry_log: ChemistryEntry[]
  processing_notes: string | null
  created_at: string
  updated_at: string
}

export interface ChemistryEntry {
  zone: string
  brand: string
  formula: string
  dev_time?: number
}

type CreateDNAInput = Omit<ClientDNARecord, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>
type UpdateDNAInput = Partial<CreateDNAInput>

/**
 * CRUD hook for client_dna_records.
 * Scoped to current tenant via TenantProvider.
 *
 * @see PERD v10.0 §6.8 — Client DNA: The Ultimate Retention Moat
 */
export function useClientDNA(clientId?: string) {
  const { tenant } = useTenant()
  const queryClient = useQueryClient()
  const tenantId = tenant?.id

  const queryKey = useMemo(
    () => ['client-dna', tenantId, clientId].filter(Boolean),
    [tenantId, clientId]
  )

  // List all DNA records for a client (or all clients in tenant)
  const {
    data: records = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      let query = supabase
        .from('client_dna_records')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('created_at', { ascending: false })

      if (clientId) {
        query = query.eq('client_id', clientId)
      }

      const { data, error } = await query
      if (error) throw error
      // JSONB → typed ChemistryEntry[]
      return (data ?? []).map((row) => ({
        ...row,
        chemistry_log: row.chemistry_log as unknown as ChemistryEntry[],
      })) as ClientDNARecord[]
    },
    enabled: !!tenantId,
  })

  // Create a new DNA record
  const createRecord = useMutation({
    mutationFn: async (input: CreateDNAInput) => {
      const { data, error } = await supabase
        .from('client_dna_records')
        .insert({
          ...input,
          tenant_id: tenantId!,
          chemistry_log: input.chemistry_log as unknown as Json,
        })
        .select()
        .single()
      if (error) throw error
      return {
        ...data,
        chemistry_log: data.chemistry_log as unknown as ChemistryEntry[],
      } as ClientDNARecord
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  // Update an existing DNA record
  const updateRecord = useMutation({
    mutationFn: async ({ id, ...updates }: UpdateDNAInput & { id: string }) => {
      const { chemistry_log, ...rest } = updates
      const payload = chemistry_log
        ? { ...rest, chemistry_log: chemistry_log as unknown as Json }
        : rest
      const { data, error } = await supabase
        .from('client_dna_records')
        .update(payload)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return {
        ...data,
        chemistry_log: data.chemistry_log as unknown as ChemistryEntry[],
      } as ClientDNARecord
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  // Delete a DNA record
  const deleteRecord = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('client_dna_records').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })

  return {
    records,
    isLoading,
    error,
    createRecord,
    updateRecord,
    deleteRecord,
  }
}
